import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

// Components
import Avatar from '../AssessorAvatar'
import JobTag from '../tag/JobTag'
import Text from '../Text'
import Icon from '../icon/Icon'
import AvatarComponent, { AvatarSize } from '../Avatar'
import UserTag from '../tag/UserTag'
import { RowAligned } from '../layout/LayoutAtoms'

// Styles
import { mqFrom } from '../../styles/mediaQueries'
import { shadows } from '../../styles/effects'

// Types
import { ICompanyMember } from '../../common/models/Company'
import { getMemberRole } from '../../common/helpers/isLeadOf'
import { TeamRole } from '../../common/enums/userCompanyRole.enum'
import { IconType } from '../icon/IconType'

interface IProps {
  version: 'small' | 'medium' | 'large' | 'noTags' | 'job'
  user?: ICompanyMember
  onClick?: () => void
  greyTags?: boolean
  icon?: IconType
  external?: {
    fullName: string
    email: string
  }
  checked?: boolean
  className?: string
}

interface IUserTagsProps {
  user?: ICompanyMember
  whiteBackground?: boolean
  withoutContainer?: boolean
  className?: string
  external?: {
    fullName: string
    email: string
  }
}

export const UserTags: React.FC<IUserTagsProps> = ({
  user,
  external,
  whiteBackground,
  className,
  withoutContainer,
}) => {
  const themeContext = useContext(ThemeContext)
  const isAdmin = user?.isAdmin
  const isLead = getMemberRole(user) === TeamRole.LEAD

  const renderTags = () => (
    <>
      {isLead && (
        <LeadTag
          whiteBackground={whiteBackground}
          color={
            whiteBackground
              ? themeContext.colors.lightest
              : themeContext.colors.primaryLight
          }
          content={
            <Icon type="Lead" size={14} color={themeContext.colors.primary} />
          }
        />
      )}
      {user?.jobLevel && (
        <StyledJobTag
          content={user.jobLevel.title}
          color={
            whiteBackground
              ? themeContext.colors.lightest
              : themeContext.colors.primaryLight
          }
        />
      )}
      {isAdmin && (
        <StyledJobTag
          content={
            <Icon type="Admin" size={13} color={themeContext.colors.primary} />
          }
          color={
            whiteBackground
              ? themeContext.colors.lightest
              : themeContext.colors.primaryLight
          }
        />
      )}
      {external && (
        <StyledJobTag
          content={external.email}
          color={
            whiteBackground
              ? themeContext.colors.lightest
              : themeContext.colors.primaryLight
          }
        />
      )}
      {user?.isDisabled && (
        <UserTag caption="Archived" color={themeContext.colors.secondary} />
      )}
    </>
  )

  return withoutContainer ? (
    renderTags()
  ) : (
    <Tags className={className}>{renderTags()}</Tags>
  )
}

const UserDetails: React.FC<IProps> = ({
  version,
  user,
  checked,
  external,
  onClick,
  icon,
  greyTags,
  className,
}) => {
  switch (version) {
    default:
    case 'noTags':
    case 'job':
      return (
        <RowAligned onClick={onClick} className={className}>
          <SmallAvatar
            external={!!external}
            assessor={user}
            isAssessmentSubmitted={false}
          />
          <div>
            <Text textStyle="labelHeavy">
              {external ? external.fullName : user?.firstName}
              {version === 'job' && (
                <Text textStyle="label" tag="span">{` ${
                  user?.preposition ? user?.preposition + ' ' : ''
                }${user?.lastName}`}</Text>
              )}
            </Text>
            {!external && (
              <Text
                textStyle="caption"
                color={version === 'job' ? 'text72' : 'textLight'}
                marginTop={version === 'job' ? 4 : 0}
              >
                {version === 'noTags'
                  ? `${user?.preposition ? user?.preposition + ' ' : ''}${
                      user?.lastName
                    }`
                  : user?.jobLevel?.title}
              </Text>
            )}
          </div>
          {icon && <StyledIcon type={icon} />}
        </RowAligned>
      )
    case 'small':
      return (
        <>
          <User onClick={onClick}>
            <Avatar
              assessor={user}
              external={!!external}
              isAssessmentSubmitted={!!checked}
            />
            <Text textStyle="labelHeavy">
              {external ? external.fullName : user?.name}
            </Text>
          </User>
          <UserTags user={user} external={external} />
        </>
      )
    case 'large':
    case 'medium':
      return (
        <User className={className} onClick={onClick}>
          <StyledAvatar
            user={user}
            size={version === 'medium' ? AvatarSize.Medium : AvatarSize.Large}
          />
          <div>
            <Text textStyle="title" marginBottom={4}>
              {external ? external.fullName : user?.name}
            </Text>
            <UserTags
              user={user}
              external={external}
              whiteBackground={!greyTags}
            />
          </div>
        </User>
      )
  }
}

export default UserDetails

const SmallAvatar = styled(Avatar)`
  border: 2px solid ${({ theme }) => theme.colors.lightest};
  box-shadow: ${shadows.depthTwo};
  margin-right: 8px;
  border-radius: 50%;
`

const StyledJobTag = styled(JobTag)`
  display: block;
`

const StyledIcon = styled(Icon)`
  margin-left: auto;
`

const StyledAvatar = styled(AvatarComponent)`
  border: 4px solid ${({ theme }) => theme.colors.lightest};
  box-shadow: ${shadows.depthThree};
  margin-right: 12px;
  flex-shrink: 0;
`

const User = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 4px;

  ${mqFrom.L`
    margin-right: 12px;
    margin-bottom: 0;
  `}
`

export const LeadTag = styled(JobTag)<{ whiteBackground?: boolean }>`
  z-index: 1;
  border: 1px solid
    ${({ theme, whiteBackground }) =>
      whiteBackground ? theme.colors.primaryLight : theme.colors.lightest};
`

const Tags = styled.div`
  flex-wrap: wrap;
  align-items: flex-start;
  display: flex;
  margin-bottom: 12px;
  min-width: 0;

  > * {
    margin: 2px 0;

    &:not(:last-child) {
      margin: 2px 8px 2px 0;
    }
  }

  > ${LeadTag} {
    margin: 1px -4px 1px 0;
  }

  ${mqFrom.L`
    margin-bottom: 0;
  `}
`
